import { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Text } from 'react-native'

import { i18n } from '@localization'
import { useNavigation, useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import {
  useAddCustomerNextStateWeb,
  useAgentDetailsWF,
  useGetAgentBanners,
} from '@zet/customer/src/api/hooks'
import AddCustomerHeader from '@zet/customer/src/components/addCustomerHeader'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Checkbox } from '@zet/design-system/checkbox'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B1, B2, H5, SH3 } from '@zet/design-system/typography'
import {
  experianConsent,
  privacyPolicyForExperianLink,
  privacyPolicyLink,
  termsAndConditionsForExperianLink,
  termsAndConditionsLink,
} from '@zet/feature-brands/utils/constants'
import { styled } from 'nativewind'

import KeyBoardWrapper from '@components/keyboardWrapper'
import OtpInput from '@components/otpInput'

import CustomImageHeader from '../../../components/bannerHeader'

const BoldText = styled(Text, 'font-[Graphie-Bold] text-sm leading-5')

const OtpScreen = () => {
  const { mutate: mutateWithExperiments, isLoading: isMutateLoading } =
    useAddCustomerNextStateWeb()
  const { data: agentData, isLoading } = useAgentDetailsWF()
  const { data: bannerData, isLoading: isBannerLoading } = useGetAgentBanners({
    onecode: agentData?.onecode,
  })
  const isCreditScoreCheckFlow =
    sessionStorage.getItem('isCreditScoreCheck') === 'true'
  const [value, setValue] = useState({})
  const [timer, setTimer] = useState<number>(30)
  const [isConsentGiven, setIsConsentGiven] = useState<boolean>(false)
  const route = useRoute()
  const { customerPhoneNumber, customerId, customerName, isWebFlow } =
    useAddCustomerStore((store) => store)
  const isExperianEnabled = route?.params?.isExperianEnabled

  const isReconsentFlow = route?.params?.isReconsentFlow?.toString() === 'true'
  const navigation = useNavigation()

  useEffect(() => {
    navigation.setOptions({
      header: () => (!bannerData?.imageUrl ? <AddCustomerHeader /> : <></>),
    })
  }, [bannerData?.imageUrl])

  useEffect(() => {
    if (!isWebFlow && route?.params?.customerId) {
      mutateWithExperiments({
        customerId: route?.params?.customerId,
        screenName: 'GET_CURRENT_STATE',
        fullName: route?.params?.customerName,
        phoneNumber: route?.params?.customerPhoneNumber,
      })
    }
  }, [mutateWithExperiments, route, isWebFlow])

  useEffect(() => {
    if (isReconsentFlow) {
      publishEvent('screen_view', {
        screen_name: 'brands_list_recommendation_reconsent_web_otp_screen',
      })
    }
  })

  const handleSubmit = () => {
    mutateWithExperiments({
      customerId: route?.params?.customerId || customerId,
      otp: Object.values(value).join(''),
      phoneNumber: route?.params?.customerPhoneNumber || customerPhoneNumber,
      screenName: 'SHOWN_OTP_SCREEN',
      isReconsentFlow: isReconsentFlow,
      isCreditCheck: isCreditScoreCheckFlow,
      fullName: route?.params?.customerName || customerName,
    })
  }

  const handleButtonDisablity = () => {
    const isValueLengthValid = Object.values(value).join('').length === 4

    if (!isValueLengthValid) {
      return true
    }

    return isExperianEnabled && !isConsentGiven
  }

  const resendOtp = () => {
    const requestBodyData = {
      fullName: route?.params?.customerName || customerName,
      phoneNumber:
        route?.params?.customerPhoneNumber || Number(customerPhoneNumber),
      screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
      isReconsentFlow,
    }

    mutateWithExperiments(requestBodyData)
  }

  const interval = 1000
  const timerRef = useRef(0)
  let prev = 0
  let diff = 0
  const timerCallback = () => {
    const date = new Date()
    setTimer((i) => {
      const current = date.getSeconds()
      if (diff === 0) {
        diff = 1
        prev = date.getSeconds()
        return 30
      }

      if (prev > current) diff = 60 - prev + current
      else diff = current - prev

      prev = date.getSeconds()

      if (i > 1)
        if (i + diff < 1) {
          clearInterval(timerRef.current)
          return 1
        } else {
          return i - diff
        }
      else {
        clearInterval(timerRef.current)
        return 0
      }
    })
  }
  const timerFunction = () => {
    timerRef.current = setInterval(timerCallback, interval)
  }

  const otpTimer = useCallback(timerFunction, [timerRef])
  function openTermsAndConditions() {
    window.open(
      isExperianEnabled
        ? termsAndConditionsForExperianLink
        : termsAndConditionsLink
    )
  }
  function openPrivacyPolicy() {
    window.open(
      isExperianEnabled ? privacyPolicyForExperianLink : privacyPolicyLink
    )
  }
  function openExperianTermsAndConditions() {
    window.open(experianConsent)
  }

  useEffect(() => {
    otpTimer()
    return () => {
      clearInterval(timerRef.current)
    }
  }, [])

  if (isLoading || isBannerLoading) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ActivityIndicator size={'large'} color={'blue'} />
      </Stack>
    )
  }

  return (
    <KeyBoardWrapper
      className="flex-1 bg-white"
      BottomSheet={
        isReconsentFlow && !isExperianEnabled ? (
          <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
            <Button
              className="w-full rounded py-[12]"
              disabled={handleButtonDisablity()}
              onPress={handleSubmit}
              loading={isMutateLoading}
              eventName="click_action"
              eventPayload={{
                item_name: 'add_customer_step_1_continue',
                ...(route.params?.customerPhoneNumber || customerPhoneNumber
                  ? {
                      customer_phone:
                        route.params?.customerPhoneNumber ||
                        customerPhoneNumber,
                    }
                  : {}),
                ...(route.params?.customerId || customerId
                  ? { customer_id: route.params?.customerId || customerId }
                  : {}),
              }}
            >
              {i18n.t('reconsent.continue')}
            </Button>
          </Stack>
        ) : (
          <>
            {isExperianEnabled ? (
              <XStack className="mx-4 items-center">
                <Stack className="mr-3">
                  <Checkbox
                    accesibilityLabel="consentToTnc"
                    checked={isConsentGiven}
                    onChange={setIsConsentGiven}
                  />
                </Stack>
                <>
                  <B2>
                    {i18n.t('webFlow.otpScreen.iAgree')}
                    <Pressable
                      onPress={openPrivacyPolicy}
                      className=" ml-1 justify-center"
                      eventName="click_action"
                      eventPayload={{
                        item_name: isReconsentFlow
                          ? 'brands_list_recommendation_reconsent_web_privacy_policy'
                          : 'add_customer_privacy_policy',
                      }}
                    >
                      <BoldText>
                        {i18n.t('verifyPhNum.privacyPolicy')}{' '}
                      </BoldText>
                    </Pressable>
                    {i18n.t('webFlow.otpScreen.and')}
                    <Pressable
                      onPress={openTermsAndConditions}
                      className=" ml-1 justify-center"
                      eventName="click_action"
                      eventPayload={{
                        item_name: isReconsentFlow
                          ? 'brands_list_recommendation_reconsent_web_terms_of_use'
                          : 'add_customer_terms_of_use',
                      }}
                    >
                      <BoldText>{i18n.t('webFlow.otpScreen.terms')}</BoldText>
                    </Pressable>{' '}
                    {i18n.t('webFlow.otpScreen.zet')}
                    <Pressable
                      onPress={openExperianTermsAndConditions}
                      className=" ml-1 justify-center"
                      eventName="click_action"
                      eventPayload={{
                        item_name: isReconsentFlow
                          ? 'brands_list_recommendation_reconsent_web_experian_terms_and_conditions'
                          : 'add_customer_experian_terms_and_conditions',
                      }}
                    >
                      <BoldText>{i18n.t('webFlow.otpScreen.t&c')}</BoldText>
                    </Pressable>{' '}
                    {i18n.t('webFlow.otpScreen.experian')}
                  </B2>
                </>
              </XStack>
            ) : (
              <Stack className="mb-2 flex px-3">
                <B2>
                  {i18n.t('webFlow.otpScreen.byClicking')}
                  <Pressable
                    onPress={openTermsAndConditions}
                    className=" ml-1 justify-center"
                    eventName="click_action"
                    eventPayload={{
                      item_name: isReconsentFlow
                        ? 'brands_list_recommendation_reconsent_web_terms_of_use'
                        : 'add_customer_terms_of_use',
                    }}
                  >
                    <BoldText>{i18n.t('verifyPhNum.terms')}</BoldText>
                  </Pressable>
                  <B2> {i18n.t('verifyPhNum.and')} </B2>

                  <Pressable
                    onPress={openPrivacyPolicy}
                    eventName="click_action"
                    className="justify-center"
                    eventPayload={{
                      item_name: isReconsentFlow
                        ? 'brands_list_recommendation_reconsent_web_privacy_policy'
                        : 'add_customer_privacy_policy',
                    }}
                  >
                    <BoldText>{i18n.t('verifyPhNum.privacyPolicy')}.</BoldText>
                  </Pressable>
                </B2>
              </Stack>
            )}
            <Stack className="border-t-[1px] border-[#ECEFF5] px-3 py-2">
              <Button
                size="lg"
                className={`w-full rounded py-[12]`}
                disabled={handleButtonDisablity() || isMutateLoading}
                onPress={handleSubmit}
                buttonEvent={{
                  eventName: 'click_action',
                  payload: { item_name: 'add_customer_step_1_continue' },
                }}
                style={{
                  backgroundColor:
                    handleButtonDisablity() && zetColors.grays[50],
                }}
                loading={!handleButtonDisablity() && isMutateLoading}
                loaderText={i18n.t('customerDetails.verifying')}
              >
                {isReconsentFlow
                  ? i18n.t('reconsent.continue')
                  : i18n.t('customerDetails.verify')}
              </Button>
            </Stack>
          </>
        )
      }
    >
      <>
        {bannerData?.imageUrl && (
          <CustomImageHeader bannerUrl={bannerData?.imageUrl} />
        )}
        <Stack className="-mt-3 rounded-tl-xl rounded-tr-xl bg-white pt-3">
          {bannerData?.imageUrl && (
            <H5 className="text-grays-80 mx-3 font-[Graphie-Bold]">
              Verify Your Phone Number
            </H5>
          )}
          <B1 className="text-grays-60 mx-3 mt-2">
            {i18n.t('webFlow.otpScreen.weHave')}
            <B1 className="text-grays-100 font-[Graphie-Bold]">
              {' '}
              {route?.params?.customerPhoneNumber || customerPhoneNumber}
            </B1>
            .{i18n.t('webFlow.otpScreen.fillThe')}
          </B1>
          <Stack>
            <OtpInput value={value} setValue={setValue} otpCount={4} />
          </Stack>
          <Stack className="mt-4 items-center">
            {timer === 0 ? (
              <Pressable
                onPress={() => {
                  prev = 0
                  diff = 0
                  timerFunction()
                  resendOtp()
                }}
                eventName="click_action"
                eventPayload={{
                  item_name: isReconsentFlow
                    ? 'brands_list_recommendation_reconsent_web_resend_otp'
                    : 'add_customer_resend_otp',
                }}
              >
                <SH3 className="text-grays-60">
                  {i18n.t('webFlow.otpScreen.didNot')}
                  <SH3 className="text-primary-100 font-[Graphie-Bold]">
                    {i18n.t('webFlow.otpScreen.resend')}
                  </SH3>
                </SH3>
              </Pressable>
            ) : (
              <B2 className=" text-grays-60 text-center">
                {`Resend OTP in `}
                <SH3 className="text-primary-100">
                  00:{timer < 10 ? `0${timer}` : timer}
                </SH3>
              </B2>
            )}
          </Stack>
        </Stack>
      </>
    </KeyBoardWrapper>
  )
}

export default OtpScreen
